import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function UnfilteredQueriesPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Unfiltered Queries</h1>
          <p className="text-lg text-gray-600 mt-2">
            Why querying all records is dangerous and how to fix it
          </p>
        </div>

        {/* Critical Alert */}
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
          <div className="flex">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Performance and Governance Risk</h3>
              <p className="mt-2 text-sm text-red-700">
                Unfiltered queries attempt to retrieve ALL records from an object, which can cause
                severe performance degradation, timeout errors, and memory issues. In production
                orgs with large data volumes, this will cause your flow to fail.
              </p>
            </div>
          </div>
        </div>

        {/* What is it? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Unfiltered Queries?</h2>
          <p className="text-gray-700 mb-4">
            An unfiltered query is a Get Records element that has no filter conditions - it attempts
            to retrieve every single record from an object. This is almost always a mistake.
          </p>

          <div className="bg-gray-100 p-4 rounded mb-4">
            <p className="text-sm font-semibold text-gray-900 mb-2">Example of Unfiltered Query:</p>
            <div className="font-mono text-sm text-gray-700">
              Get Records: Accounts<br />
              <span className="text-red-600">Filter Conditions: (none)</span><br />
              Store in: AllAccounts
            </div>
          </div>

          <p className="text-gray-700">
            If your org has 100,000 Accounts, this query will attempt to retrieve all 100,000 records,
            which will almost certainly fail or timeout.
          </p>
        </div>

        {/* Why is it bad? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Are Unfiltered Queries Problematic?</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-gray-900">Query Row Limit Violations</h3>
              <p className="text-gray-700 mt-1">
                Salesforce limits SOQL queries to 50,000 rows per transaction. Unfiltered queries
                on large objects will exceed this limit and fail with "Too many query rows: 50,001"
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">Memory Issues</h3>
              <p className="text-gray-700 mt-1">
                Attempting to load tens of thousands of records into memory can cause heap size
                errors (6 MB limit) or cause the flow to run extremely slowly.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="font-semibold text-gray-900">Timeout Errors</h3>
              <p className="text-gray-700 mt-1">
                Queries that take too long to execute will timeout, causing the flow to fail.
                Unfiltered queries on large objects often hit the 10-minute CPU time limit.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-900">Unpredictable Behavior</h3>
              <p className="text-gray-700 mt-1">
                A flow that works in a test org with 100 records will fail spectacularly in
                production with 100,000 records. This creates deployment risks.
              </p>
            </div>
          </div>
        </div>

        {/* How to identify */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify Unfiltered Queries</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check the Performance Scorecard on the flow detail page</li>
                <li>Look for "Unfiltered Query" warnings</li>
                <li>Review Get Records elements in the Flow Elements section</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In Salesforce Flow Builder:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Open each Get Records element</li>
                <li>Check the "Filter Records" section</li>
                <li>If it says "All records" or has no conditions, it's unfiltered</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Common Scenarios:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Accidentally selecting "All records" instead of adding filter conditions</li>
                <li>Removing filter conditions during testing and forgetting to add them back</li>
                <li>Not realizing the query needs filtering for production data volumes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* The Fix */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Fix Unfiltered Queries</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Solution</h3>
                <p className="mt-2 text-sm text-green-700">
                  Add meaningful filter conditions to limit the query to only the records you actually
                  need. Use indexed fields when possible for better performance.
                </p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mb-3">Step-by-Step Fix:</h3>

          <ol className="space-y-6">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Identify What Records You Actually Need</h4>
                  <p className="text-gray-700 mt-1">
                    Ask yourself: "Do I really need ALL records, or just specific ones?"
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-gray-700">
                    <p><strong>Examples of what you might actually need:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Records owned by the current user</li>
                      <li>Records created in the last 30 days</li>
                      <li>Records matching specific criteria (Status, Type, etc.)</li>
                      <li>Records related to the triggering record</li>
                    </ul>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Add Filter Conditions</h4>
                  <p className="text-gray-700 mt-1">
                    In the Get Records element, switch from "All Records" to "Specify Conditions"
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 text-sm">
                    <p className="font-semibold mb-2">Good filter examples:</p>
                    <div className="space-y-1 font-mono">
                      <div>OwnerId Equals {'{!$User.Id}'}</div>
                      <div>CreatedDate Greater Than or Equal {'{!Last30Days}'}</div>
                      <div>Status__c Equals "Active"</div>
                      <div>AccountId Equals {'{!triggeredAccount.Id}'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Use Indexed Fields When Possible</h4>
                  <p className="text-gray-700 mt-1">
                    For better performance, filter on indexed fields:
                  </p>
                  <div className="mt-2 bg-blue-50 p-3 rounded text-sm">
                    <p className="font-semibold text-blue-900 mb-1">Indexed fields include:</p>
                    <ul className="list-disc list-inside text-blue-800 ml-2 space-y-1">
                      <li>Id</li>
                      <li>Name</li>
                      <li>OwnerId</li>
                      <li>CreatedDate</li>
                      <li>SystemModstamp</li>
                      <li>RecordTypeId</li>
                      <li>Master-detail relationships</li>
                      <li>External ID fields</li>
                    </ul>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Add a Record Limit</h4>
                  <p className="text-gray-700 mt-1">
                    Even with filters, add a "Maximum Records to Store" limit as a safety measure
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Maximum Records to Store: <span className="text-blue-600">1000</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">
                    This prevents unexpected query growth if your filters return more records than expected
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  5
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Test with Production-Like Data</h4>
                  <p className="text-gray-700 mt-1">
                    Test your flow in a sandbox with data volumes similar to production to ensure
                    your filters are restrictive enough
                  </p>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (Unfiltered)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">Get Records: Opportunities</div>
                <div className="pl-4 text-red-600">Filter: All Records</div>
                <div className="pl-4">Store in: AllOpportunities</div>
              </div>
              <div className="mt-3 text-sm text-red-700">
                Result: Attempts to load 50,000+ records, exceeds query row limit, flow fails
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (Filtered)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">Get Records: Opportunities</div>
                <div className="pl-4 text-green-600">Filter: OwnerId = $User.Id</div>
                <div className="pl-4 text-green-600">AND Stage = "Prospecting"</div>
                <div className="pl-4">Store in: MyActiveOpps</div>
                <div className="pl-4 text-blue-600">Max Records: 200</div>
              </div>
              <div className="mt-3 text-sm text-green-700">
                Result: Loads only relevant records (~20), executes quickly, stays within limits
              </div>
            </div>
          </div>
        </div>

        {/* Special Cases */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What If I Really Need All Records?</h2>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <p className="text-sm text-yellow-800">
              If you legitimately need to process all records in an object, Flows are NOT the right tool.
              Consider these alternatives:
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-medium mr-3 mt-1">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Scheduled Apex Batch Job</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Batch Apex can process millions of records by splitting them into smaller chunks.
                  This is the recommended approach for bulk data operations.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-medium mr-3 mt-1">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Data Loader</h3>
                <p className="text-gray-700 text-sm mt-1">
                  For one-time data migrations or updates, use Data Loader which can handle
                  millions of records efficiently.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-medium mr-3 mt-1">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Process Multiple Small Batches</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Create a scheduled flow that processes a limited number of records at a time
                  (e.g., "where Last_Processed__c is null, limit 1000") and runs repeatedly.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Improvement</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">Performance: 100x faster</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Before: Attempting to load 50,000+ records (fails)</li>
              <li>After: Loading only needed records (50-1000)</li>
            </ul>
            <p className="mt-4">
              Proper query filtering ensures your flow works reliably in production environments
              regardless of data volume growth.
            </p>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/indexed-queries`} className="text-blue-600 hover:text-blue-800 font-medium">
                Using Indexed Queries →
              </Link>
              <p className="text-gray-600 text-sm mt-1">How to optimize query performance with indexes</p>
            </li>
            <li>
              <Link to={`${basePath}/help/queries-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                Queries in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Another common query performance issue</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to high-performance flows</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
